import { useState } from "react";
import { ISSUE_WAITING_ON_PATTERN, isValidIsoCalendarDate, type IssueBlockerAttention } from "@paperclipai/shared";
import { cn } from "../lib/utils";
import { issueStatusLabelOverride } from "../lib/status-colors";
import { StatusGlyph, type StatusGlyphSize } from "./StatusGlyph";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

const allStatuses = ["backlog", "todo", "in_progress", "in_review", "done", "cancelled", "blocked", "waiting"];

function statusLabel(status: string): string {
  const override = issueStatusLabelOverride[status];
  if (override) return override;
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

interface StatusIconProps {
  status: string;
  blockerAttention?: IssueBlockerAttention | null;
  onChange?: (status: string) => void;
  className?: string;
  showLabel?: boolean;
  /** Glyph size (PAP-243a). Default `md` (16px); lists/detail/mentions use `lg` (20px). */
  size?: StatusGlyphSize;
  /**
   * [soltia] Enables the `waiting` option in the picker. Entering `waiting` needs
   * `waitingOn` + `waitingUntil` (always, max 365 days), so it goes through an inline
   * form and this callback instead of `onChange`. Without it, `waiting` is not offered.
   */
  onWaitingChange?: (data: { waitingOn: string; waitingUntil: string }) => void;
}

/** [soltia] waitingUntil window: today..today+365 in Europe/Madrid (same rule as the server). */
function waitingDateWindow(now = new Date()) {
  const min = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Madrid", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(now);
  const [y, m, d] = min.split("-").map(Number) as [number, number, number];
  const max = new Date(Date.UTC(y, m - 1, d + 365)).toISOString().slice(0, 10);
  return { min, max };
}

/** [soltia] Inline form to park an issue in `waiting` (on a third party or until a date). */
function WaitingForm({ onSubmit, onCancel }: {
  onSubmit: (data: { waitingOn: string; waitingUntil: string }) => void;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<"tercero" | "fecha">("tercero");
  const [who, setWho] = useState("");
  const [until, setUntil] = useState("");
  const waitingOn = kind === "fecha" ? "fecha" : `tercero:${who.trim()}`;
  const { min, max } = waitingDateWindow();
  // The date is mandatory for both kinds (Julio, 29-sep) and limited to 365 days.
  const untilOk = isValidIsoCalendarDate(until) && until >= min && until <= max;
  const valid = ISSUE_WAITING_ON_PATTERN.test(waitingOn) && untilOk;
  return (
    <form
      className="flex flex-col gap-2 p-1 text-xs"
      aria-label="Poner en espera"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) onSubmit({ waitingOn, waitingUntil: until });
      }}
    >
      <div className="flex gap-1" role="radiogroup" aria-label="Esperando a">
        {(["tercero", "fecha"] as const).map((k) => (
          <Button key={k} type="button" size="sm" variant={kind === k ? "secondary" : "ghost"}
            role="radio" aria-checked={kind === k} className="flex-1 text-xs" onClick={() => setKind(k)}>
            {k === "tercero" ? "Tercero" : "Fecha"}
          </Button>
        ))}
      </div>
      {kind === "tercero" ? (
        <input className="rounded border bg-transparent px-2 py-1" placeholder="¿A quién?" aria-label="Quién"
          maxLength={64} value={who} onChange={(e) => setWho(e.target.value)} autoFocus />
      ) : null}
      <input type="date" className="rounded border bg-transparent px-2 py-1" value={until} required
        min={min} max={max} aria-label="Hasta (obligatorio, máx. 365 días)" onChange={(e) => setUntil(e.target.value)} />
      <div className="flex justify-end gap-1">
        <Button type="button" size="sm" variant="ghost" className="text-xs" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" size="sm" className="text-xs" disabled={!valid}>Guardar</Button>
      </div>
    </form>
  );
}

function blockedAttentionLabel(blockerAttention: IssueBlockerAttention | null | undefined) {
  if (!blockerAttention || blockerAttention.state === "none") return "Blocked";

  if (blockerAttention.reason === "active_child") {
    const count = blockerAttention.coveredBlockerCount;
    if (count === 1 && blockerAttention.sampleBlockerIdentifier) {
      return `Blocked · waiting on active sub-task ${blockerAttention.sampleBlockerIdentifier}`;
    }
    if (count === 1) return "Blocked · waiting on 1 active sub-task";
    return `Blocked · waiting on ${count} active sub-tasks`;
  }

  if (blockerAttention.reason === "active_dependency") {
    const count = blockerAttention.coveredBlockerCount;
    if (count === 1 && blockerAttention.sampleBlockerIdentifier) {
      return `Blocked · covered by active dependency ${blockerAttention.sampleBlockerIdentifier}`;
    }
    if (count === 1) return "Blocked · covered by 1 active dependency";
    return `Blocked · covered by ${count} active dependencies`;
  }

  if (blockerAttention.reason === "stalled_review") {
    const count = blockerAttention.stalledBlockerCount;
    const leaf = blockerAttention.sampleStalledBlockerIdentifier ?? blockerAttention.sampleBlockerIdentifier;
    if (count === 1 && leaf) return `Blocked · review stalled on ${leaf}`;
    if (count === 1) return "Blocked · review stalled with no clear next step";
    return `Blocked · ${count} reviews stalled with no clear next step`;
  }

  if (blockerAttention.reason === "attention_required") {
    const count = blockerAttention.attentionBlockerCount || blockerAttention.unresolvedBlockerCount;
    const attentionCopy = `${count} ${count === 1 ? "blocker needs" : "blockers need"} attention`;
    const coveredCount = blockerAttention.coveredBlockerCount;
    if (coveredCount > 0) {
      return `Blocked · ${attentionCopy}; ${coveredCount} covered by active work`;
    }
    return `Blocked · ${attentionCopy}`;
  }

  return "Blocked";
}

/**
 * Task/issue status indicator — renders the unified, color-blind-safe
 * {@link StatusGlyph} (one distinct shape per status). With `onChange` it also
 * acts as a status picker (popover). This one component drives every standalone
 * status surface: list, kanban, detail header, properties row + picker flyout,
 * sub-task / blocked-by pills, blocked inbox, quicklook, sibling nav, filters,
 * search, columns, dashboard.
 *
 * A "covered" blocked task (waiting on active work) maps to the `in_queue`
 * glyph — the blocked shape recoloured blue — while the full blocked reason
 * still rides on the accessible label.
 */
export function StatusIcon({ status, blockerAttention, onChange, className, showLabel, size = "md", onWaitingChange }: StatusIconProps) {
  const [open, setOpen] = useState(false);
  const [waitingFormOpen, setWaitingFormOpen] = useState(false);
  const isCoveredBlocked = status === "blocked" && blockerAttention?.state === "covered";
  const ariaLabel = status === "blocked" ? blockedAttentionLabel(blockerAttention) : statusLabel(status);
  const glyphStatus = isCoveredBlocked ? "in_queue" : status;

  const glyph = (
    <StatusGlyph
      status={glyphStatus}
      size={size}
      className={cn(onChange && !showLabel && "cursor-pointer", className)}
      title={ariaLabel}
    />
  );

  if (!onChange) {
    return showLabel ? (
      <span className="inline-flex items-center gap-1.5">
        {glyph}
        <span className="text-sm">{statusLabel(status)}</span>
      </span>
    ) : (
      glyph
    );
  }

  const trigger = showLabel ? (
    <button
      type="button"
      aria-label={`Change status (current: ${ariaLabel})`}
      className="inline-flex min-h-5 items-center gap-1.5 cursor-pointer hover:bg-accent/50 rounded px-1 -mx-1 py-0.5 transition-colors"
    >
      {glyph}
      <span className="text-sm">{statusLabel(status)}</span>
    </button>
  ) : (
    <button
      type="button"
      data-slot="icon-button"
      aria-label={`Change status (current: ${ariaLabel})`}
      className="inline-flex cursor-pointer items-center justify-center rounded-sm focus-visible:outline-none focus-visible:ring-(length:--rad-3) focus-visible:ring-ring"
    >
      {glyph}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) setWaitingFormOpen(false); }}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className={waitingFormOpen ? "w-56 p-1" : "w-40 p-1"} align="start">
        {waitingFormOpen && onWaitingChange ? (
          <WaitingForm
            onCancel={() => setWaitingFormOpen(false)}
            onSubmit={(data) => {
              onWaitingChange(data);
              setWaitingFormOpen(false);
              setOpen(false);
            }}
          />
        ) : allStatuses.filter((s) => s !== "waiting" || onWaitingChange).map((s) => (
          <Button
            key={s}
            variant="ghost"
            size="sm"
            className={cn("w-full justify-start gap-2 text-xs", s === status && "bg-accent")}
            onClick={() => {
              if (s === "waiting") {
                setWaitingFormOpen(true);
                return;
              }
              onChange(s);
              setOpen(false);
            }}
          >
            <StatusIcon status={s} size="lg" />
            {statusLabel(s)}
          </Button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
